import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Tier } from '@foundry/shared';
import { apiFetch } from './client.js';
import type { CallRecord, CallStatus, EndUser } from './types.js';

export function useEndUsers() {
  return useQuery({
    queryKey: ['end-users'],
    queryFn: () => apiFetch<{ users: EndUser[] }>('/users').then((r) => r.users),
  });
}

export function useUpdateEndUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      tier?: Tier;
      blocked?: boolean;
    }) =>
      apiFetch<{ user: EndUser }>(`/users/${input.id}`, {
        method: 'PATCH',
        body: {
          ...(input.email !== undefined ? { email: input.email } : {}),
          ...(input.firstName !== undefined ? { firstName: input.firstName } : {}),
          ...(input.lastName !== undefined ? { lastName: input.lastName } : {}),
          ...(input.tier !== undefined ? { tier: input.tier } : {}),
          ...(input.blocked !== undefined ? { blocked: input.blocked } : {}),
        },
      }).then((r) => r.user),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['end-users'] }),
  });
}

export function useSetEndUserPassword() {
  return useMutation({
    mutationFn: (input: { id: string; password: string }) =>
      apiFetch<{ ok: true }>(`/users/${input.id}/password`, {
        method: 'POST',
        body: { password: input.password },
      }),
  });
}

export function useUserCalls(userId: string) {
  return useQuery({
    queryKey: ['user-calls', userId],
    queryFn: () => apiFetch<{ status: CallStatus; calls: CallRecord[] }>(`/users/${userId}/calls`),
    enabled: userId.length > 0,
  });
}

export function useRecordCall(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { optionKey: string; occurredAt: string; notes?: string }) =>
      apiFetch<{ call: unknown }>(`/users/${userId}/calls`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-calls', userId] }),
  });
}

export function useDeleteCall(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId: string) =>
      apiFetch<{ ok: true }>(`/users/${userId}/calls/${callId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-calls', userId] }),
  });
}
